import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Sparkles, Image, Video, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';

const Home = () => {
  const features = [
    {
      icon: <Image className="w-8 h-8" />,
      title: 'Text to Image',
      description: 'Generate stunning images from text prompts using advanced AI models',
      link: '/ai-image',
      gradient: 'from-purple-500/20 to-pink-500/20'
    },
    {
      icon: <Image className="w-8 h-8" />,
      title: 'Image to Image',
      description: 'Transform and edit images with AI-powered variations and styles',
      link: '/ai-image',
      gradient: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: 'Text to Video',
      description: 'Create cinematic videos from your text descriptions',
      link: '/ai-video',
      gradient: 'from-green-500/20 to-emerald-500/20'
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: 'Image to Video',
      description: 'Animate your images into dynamic video content',
      link: '/ai-video',
      gradient: 'from-orange-500/20 to-red-500/20'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/5 to-transparent"></div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-8">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-white/80 text-sm">AI-Powered Creative Suite</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight">
            Create any image or video
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              you can imagine
            </span>
          </h1>
          
          <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto">
            Get cutting-edge AI image and video generation tools powered by the latest models.
            Transform your creative vision into reality.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/ai-image">
              <Button className="bg-[#F4D03F] hover:bg-[#e5c230] text-black font-semibold px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105">
                Start Now
              </Button>
            </Link>
            <Link to="/ai-image">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg rounded-full">
                View Examples
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              Advanced AI tools,
              <br />
              built for creators
            </h2>
            <p className="text-white/60 text-lg">Everything you need to bring your ideas to life</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300 hover:scale-[1.02] overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/5 rounded-xl flex items-center justify-center mb-6 text-yellow-400 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-white/60">{feature.description}</p>
                  
                  <div className="mt-6 flex items-center text-yellow-400 group-hover:translate-x-2 transition-transform duration-300">
                    <span className="text-sm font-medium">Explore</span>
                    <Zap className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 backdrop-blur-sm border border-yellow-400/20 rounded-3xl p-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
              Ready to create something amazing?
            </h2>
            <p className="text-white/60 text-lg mb-8">
              Join thousands of creators using AI to bring their visions to life
            </p>
            <Link to="/ai-image">
              <Button className="bg-[#F4D03F] hover:bg-[#e5c230] text-black font-semibold px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105">
                Start Creating Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;